import {
  Avatar,
  Button,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { RecordHeader } from "@ofqual/portal-components/components/RecordHeader";
import { NonNullableOrganisationState } from "../../redux/state";
import { CompareArrows, HighlightOff } from "@material-ui/icons";
import { isClosedForEngagement } from "../../domain/Organisation";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { AssignDialog } from "./AssignDialog";
import { UserRole } from "../../domain/userRole";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { authSelector } from "../../redux/selectors";

const useStyles = makeStyles((theme: Theme) => {
  return {
    link: {
      color: "#C2C2FF",
    },
    gridItem: {
      borderRight: "1px solid rgba(255,255,255,0.2)",
      padding: "0 15px",
      "& div": {
        padding: "5px",
      },
    },
    gridItemRight: {
      padding: "0 15px",
    },
    gridItemRightInner: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      marginTop: "10px",
    },
    gridItemRightIcon: {
      margin: "auto",
      fontSize: "60px",
      textAlign: "center",
      paddingRight: "5px",
    },
    gridItemRightText: {
      textTransform: "uppercase",
      width: "200px",
    },
    gridButton: {
      color: "#f5f5f5",
    },
  };
});

type OrganisationHeaderProps = {
  organisationState: NonNullableOrganisationState | null;
};

export const OrganisationHeader: React.FC<OrganisationHeaderProps> = ({
  organisationState: state,
}) => {
  const { isInRole } = useAuth(authSelector);
  const isAdmin = useIsAdmin();
  const classes = useStyles();
  const canAssign =
    isInRole(
      UserRole.ITAdmin,
      UserRole.OfqualAccreditationAdmin,
      UserRole.OfqualRecognitionAdmin,
      UserRole.OfqualRecognitionUser,
      UserRole.OfqualRegulatoryAdmin
    ) && state?.org.pendingRecognition;
  const [isDialogOpen, setIsOpenDialog] = useState(false);

  const isOrgClosedApplication = isClosedForEngagement(state?.org);

  const calculateInitials = (name: string): string => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("");
  };

  const getValidLink = (link: any): string => {
    if (link == null) return "";
    return link.startsWith("http://") || link.startsWith("https://")
      ? link
      : `http://${link}`;
  };

  return (
    <>
      <RecordHeader>
        <Grid
          container
          direction="row"
          justify="flex-start"
          alignItems="stretch"
          spacing={2}
          style={{ height: "100%" }}
        >
          <Grid className={classes.gridItem}>
            <div>
              <label>
                <strong>Acronym:</strong>
              </label>{" "}
              <span data-test="metadata-acronym">{state?.org.acronym}</span>
            </div>
            <div>
              <label>
                <strong>Organisation Number:</strong>
              </label>{" "}
              <span data-test="metadata-companyRegNumber">
                {state?.org.companyNumber}
              </span>
            </div>
            <div>
              <label>
                <strong>Recognition Number:</strong>
              </label>{" "}
              <span data-test="metadata-recognitionNumber">
                {state?.org.recognitionNumber}
              </span>
            </div>
          </Grid>
          <Grid
            className={classes.gridItem}
            style={
              isAdmin && state?.org.organisationChanges.length
                ? { flex: 1 }
                : undefined
            }
          >
            <div>
              <label>
                <strong>Email:</strong>
              </label>{" "}
              <span>
                <a
                  data-test="metadata-email"
                  href={`mailto:${state?.org.email}`}
                  className={classes.link}
                >
                  {state?.org.email}
                </a>
              </span>
            </div>
            <div>
              <label>
                <strong>Website:</strong>
              </label>{" "}
              <span data-test="metadata-website">
                <a
                  target="_blank"
                  rel="noreferrer"
                  data-test="metadata-website"
                  href={getValidLink(state?.org.website)}
                  className={classes.link}
                >
                  {state?.org.website}
                </a>
              </span>
            </div>
            <div>
              <label>
                <strong>List of Standard Fees:</strong>
              </label>{" "}
              <span>
                <a
                  target="_blank"
                  rel="noreferrer"
                  data-test="metadata-listOfStandardFees"
                  href={getValidLink(state?.org.listOfStandardFeesUrl)}
                  className={classes.link}
                >
                  {state?.org.listOfStandardFeesUrl}
                </a>
              </span>
            </div>
          </Grid>
          {canAssign && (
            <Grid className={classes.gridItem}>
              <Button
                onClick={() => setIsOpenDialog(true)}
                data-test="org-assigned-to"
                className={classes.gridButton}
              >
                <Avatar
                  style={{
                    backgroundColor: "#293445",
                    width: 70,
                    height: 70,
                    fontSize: 30,
                  }}
                >
                  {state?.org.assignedToDisplayName
                    ? calculateInitials(state?.org.assignedToDisplayName)
                    : ""}
                </Avatar>
                {state?.org.assignedToUpn ? (
                  <div>
                    <label>
                      <strong>Assigned to:</strong>
                    </label>
                    <br></br>
                    <span data-test="metadata-assigned-to">
                      {state?.org.assignedToDisplayName
                        ? state?.org.assignedToDisplayName
                        : state?.org.assignedToUpn
                        ? state?.org.assignedToUpn
                        : "Unassigned"}
                    </span>
                  </div>
                ) : (
                  <div>
                    <label>
                      <strong>Assign to</strong>
                    </label>
                  </div>
                )}
              </Button>
            </Grid>
          )}

          {isAdmin && state?.org.organisationChanges.length ? (
            <Grid className={classes.gridItemRight} data-test="pending-changes">
              <div className={classes.gridItemRightInner}>
                <CompareArrows className={classes.gridItemRightIcon} />
                <Typography
                  variant="subtitle2"
                  className={classes.gridItemRightText}
                >
                  {state.org.organisationChanges.length} pending{" "}
                  {state.org.organisationChanges.length > 1
                    ? "changes require "
                    : "change requires "}
                  approval
                </Typography>
              </div>
            </Grid>
          ) : null}

          {isOrgClosedApplication ? (
            <Grid className={classes.gridItemRight} data-test="closed-message">
              <div className={classes.gridItemRightInner}>
                <HighlightOff className={classes.gridItemRightIcon} />
                <Typography
                  variant="subtitle2"
                  className={classes.gridItemRightText}
                >
                  This Organisation is Closed
                </Typography>
              </div>
            </Grid>
          ) : null}
        </Grid>
      </RecordHeader>
      <AssignDialog
        organisation={state?.org!}
        onClose={() => setIsOpenDialog(false)}
        open={isDialogOpen}
      />
    </>
  );
};
